/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Run entity attachment uploads and deletes through UploadManager
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import QUERY_KEYS from 'app-constants/queryKeys';
import api from 'client/index';
import { useUploadManagerActions } from 'store/uploadManager';
import AppEvents, { Events } from '@veritable/common/utils/appEvents';
import { FHIR_ENTITY_TYPES } from '@veritable/common/constants';
import { DEFAULT_CONTENT_TYPE, getUploadErrorMessage, uploadFileToPresignedUrl } from 'utils/uploadAttachment';

type RunAttachmentOperationsParams = {
	files?: File[];
	blobIdsToDelete?: string[];
};

type UseRunAttachmentOperationsParams = {
	entityType: EntityTypeEnum;
	entityId: string;
	getDeleteFileName?: (blobId: string) => string;
};

type UseRunAttachmentOperationsResult = {
	runAttachmentOperations: (params: RunAttachmentOperationsParams) => Promise<void>;
};

const useRunAttachmentOperations = ({
	entityType,
	entityId,
	getDeleteFileName,
}: UseRunAttachmentOperationsParams): UseRunAttachmentOperationsResult => {
	const queryClient = useQueryClient();
	const { startUpload, startDelete, updateUpload } = useUploadManagerActions();

	const invalidateQueries = useCallback(async () => {
		const queries = [queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allAttachments, { id: entityId, entityType }] })];
		if (entityType === FHIR_ENTITY_TYPES.paymentReconciliation) {
			queries.push(
				queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentAllocation, { id: entityId }] }),
				queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allPaymentAllocations] }),
			);
		}
		await Promise.all(queries);
	}, [entityId, entityType, queryClient]);

	const runUploads = useCallback(
		async (files: File[]): Promise<number> => {
			if (_.isEmpty(files)) {
				return 0;
			}

			const uploadIds = _.map(files, (file) => startUpload({ fileName: file.name, size: file.size, entityId, entityType }));
			_.forEach(uploadIds, (uploadId) => updateUpload(uploadId, { status: 'registering' }));

			let uploadResponses: PresignedUrlResponse[];
			try {
				const { data } = await api.AttachmentApi.uploadEntityAttachments({
					entityType,
					entityId,
					uploadMultipleFiles: {
						files: _.map(files, (file) => ({ fileName: file.name, fileType: file.type || DEFAULT_CONTENT_TYPE })),
					},
				});
				uploadResponses = data;
			} catch (error) {
				_.forEach(files, (file, index) => {
					const errorMessage = getUploadErrorMessage(error, file.name);
					updateUpload(uploadIds[index], { status: 'failed', error: errorMessage });
				});
				AppEvents.publish(Events.Error, getUploadErrorMessage(error, 'attachments'));
				return 0;
			}

			const results = await Promise.allSettled(
				_.map(files, async (file, index) => {
					const uploadResponse = uploadResponses[index];
					if (!uploadResponse?.presignedUrl) {
						throw new Error(`Upload details missing for ${file.name}`);
					}

					updateUpload(uploadIds[index], { status: 'uploading', progress: 0 });
					try {
						await uploadFileToPresignedUrl({
							file,
							presignedUrl: uploadResponse.presignedUrl,
							onProgress: (progress) => updateUpload(uploadIds[index], { status: 'uploading', progress }),
						});
					} catch (error) {
						if (uploadResponse.blobId) {
							await api.AttachmentApi.deleteEntityAttachment({ entityType, entityId, blobId: uploadResponse.blobId }).catch(
								() => undefined,
							);
						}
						throw error;
					}
				}),
			);

			let successCount = 0;
			_.forEach(results, (result, index) => {
				if (result.status === 'fulfilled') {
					successCount += 1;
					updateUpload(uploadIds[index], {
						status: 'completed',
						progress: 100,
						completedAt: new Date().toISOString(),
					});
					return;
				}

				const errorMessage = getUploadErrorMessage(result.reason, files[index].name);
				updateUpload(uploadIds[index], { status: 'failed', error: errorMessage });
				AppEvents.publish(Events.Error, errorMessage);
			});

			return successCount;
		},
		[entityId, entityType, startUpload, updateUpload],
	);

	const runDeletes = useCallback(
		async (blobIds: string[]): Promise<number> => {
			if (_.isEmpty(blobIds)) {
				return 0;
			}

			const deleteIds = _.map(blobIds, (blobId) =>
				startDelete({
					fileName: getDeleteFileName?.(blobId) || blobId,
					entityId,
					entityType,
				}),
			);

			const results = await Promise.allSettled(
				_.map(blobIds, (blobId) => api.AttachmentApi.deleteEntityAttachment({ entityType, entityId, blobId })),
			);

			let successCount = 0;
			_.forEach(results, (result, index) => {
				if (result.status === 'fulfilled') {
					successCount += 1;
					updateUpload(deleteIds[index], {
						status: 'completed',
						progress: 100,
						completedAt: new Date().toISOString(),
					});
					return;
				}

				const fileName = getDeleteFileName?.(blobIds[index]) || blobIds[index];
				const errorMessage = getUploadErrorMessage(result.reason, fileName);
				updateUpload(deleteIds[index], { status: 'failed', error: errorMessage });
				AppEvents.publish(Events.Error, errorMessage);
			});

			return successCount;
		},
		[entityId, entityType, getDeleteFileName, startDelete, updateUpload],
	);

	const runAttachmentOperations = useCallback(
		async ({ files = [], blobIdsToDelete = [] }: RunAttachmentOperationsParams) => {
			if (_.isEmpty(files) && _.isEmpty(blobIdsToDelete)) {
				return;
			}

			const [uploadResult, deleteResult] = await Promise.allSettled([runUploads(files), runDeletes(blobIdsToDelete)]);
			const successCount =
				(uploadResult.status === 'fulfilled' ? uploadResult.value : 0) +
				(deleteResult.status === 'fulfilled' ? deleteResult.value : 0);

			if (successCount > 0) {
				AppEvents.publishApiSuccess(
					successCount === 1 ? '1 attachment update completed' : `${successCount} attachment updates completed`,
				);
			}

			await invalidateQueries();
		},
		[invalidateQueries, runDeletes, runUploads],
	);

	return { runAttachmentOperations };
};

export default useRunAttachmentOperations;
